import React, { useEffect, useState } from "react";
import { Sparkles, ArrowUpRight, X } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * Inline celebratory banner shown when a share-milestone WS event fires.
 * Used by GamificationContext — the parent owns the open/payload state so
 * we stay stateless here. CSS-only confetti = zero deps, 60fps.
 */
export default function ShareMilestoneBurst({ payload, onDismiss }) {
    const [exiting, setExiting] = useState(false);

    useEffect(() => {
        if (!payload) return;
        // Auto-dismiss after 7s so it never gets in the way.
        const t = setTimeout(() => {
            setExiting(true);
            setTimeout(() => onDismiss?.(), 350);
        }, 7000);
        return () => clearTimeout(t);
    }, [payload, onDismiss]);

    if (!payload) return null;
    const { target_title, threshold, emoji, link } = payload;

    return (
        <div
            data-testid="share-milestone-burst"
            className={`fixed bottom-6 right-6 z-50 max-w-sm pointer-events-none ${
                exiting ? "animate-fade-out" : "animate-slide-up"
            }`}
            style={{ animationDuration: "350ms", animationFillMode: "forwards" }}
        >
            <div className="relative pointer-events-auto bg-snowcap border-2 border-ember-orange rounded-xl shadow-2xl overflow-hidden">
                <Confetti />
                <div className="relative p-4 pr-8">
                    <button
                        type="button"
                        onClick={() => { setExiting(true); setTimeout(() => onDismiss?.(), 300); }}
                        aria-label="Dismiss"
                        data-testid="share-milestone-close"
                        className="absolute top-2 right-2 w-7 h-7 rounded-full hover:bg-trail-tan/40 flex items-center justify-center text-slate-gray"
                    >
                        <X className="w-3.5 h-3.5" />
                    </button>
                    <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-ember-orange/15 flex items-center justify-center text-2xl shrink-0 leading-none">
                            <span aria-hidden>{emoji || "🎉"}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-ember-orange mb-1">
                                Share milestone unlocked
                            </div>
                            <p className="text-sm font-bold text-midnight-pine leading-snug">
                                "{target_title}" just hit{" "}
                                <span className="text-ember-orange">{threshold} shares</span>
                            </p>
                            <p className="text-xs text-slate-gray mt-1">
                                People are actively sharing this. Ride the wave 🌊
                            </p>
                            {link && (
                                <Link
                                    to={link}
                                    onClick={() => { setExiting(true); setTimeout(() => onDismiss?.(), 300); }}
                                    data-testid="share-milestone-cta"
                                    className="inline-flex items-center gap-1 mt-2 text-xs font-bold text-ember-orange hover:underline"
                                >
                                    View activity <ArrowUpRight className="w-3 h-3" />
                                </Link>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

/**
 * 24 colored chips falling/spinning behind the card.
 * Pure CSS — zero JS animation cost.
 */
function Confetti() {
    const COLORS = ["#F26A2E", "#65A30D", "#0EA5E9", "#FCD34D", "#A855F7", "#DC2626"];
    return (
        <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden>
            {Array.from({ length: 24 }).map((_, i) => {
                const left = (i * 4.2) % 100;
                const delay = (i % 8) * 0.12;
                const dur = 1.2 + (i % 5) * 0.25;
                const color = COLORS[i % COLORS.length];
                const size = 6 + (i % 3) * 2;
                return (
                    <span
                        key={i}
                        className="absolute top-0 rounded-sm animate-confetti"
                        style={{
                            left: `${left}%`,
                            width: size,
                            height: size,
                            backgroundColor: color,
                            animationDelay: `${delay}s`,
                            animationDuration: `${dur}s`,
                        }}
                    />
                );
            })}
            <style>{`
                @keyframes confetti {
                    0%   { transform: translateY(-20px) rotate(0deg); opacity: 0; }
                    20%  { opacity: 1; }
                    100% { transform: translateY(160px) rotate(540deg); opacity: 0; }
                }
                .animate-confetti { animation-name: confetti; animation-timing-function: cubic-bezier(.4,0,.2,1); animation-fill-mode: forwards; }
                @keyframes slide-up {
                    from { transform: translateY(20px); opacity: 0; }
                    to   { transform: translateY(0); opacity: 1; }
                }
                .animate-slide-up { animation-name: slide-up; }
                @keyframes fade-out {
                    from { transform: translateY(0); opacity: 1; }
                    to   { transform: translateY(-6px); opacity: 0; }
                }
                .animate-fade-out { animation-name: fade-out; }
            `}</style>
        </div>
    );
}
